import React from "react";
import { cn } from "@/lib/utils";
import { ChatMessage } from "./ChatMessage";
import { Message } from "@/store/zustand/supabaseStore";
import { ManualAssistantMessage } from "@/store/zustand/aiContextStore";
import { RotateCcw, Loader2, Clock } from "lucide-react";
import { useRestoreMessage } from "./hooks/useRestoreMessage";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface DiscardedMessagesSectionProps {
  messages: (Message | ManualAssistantMessage)[];
  searchQuery?: string;
}

export const DiscardedMessagesSection: React.FC<
  DiscardedMessagesSectionProps
> = ({ messages, searchQuery }) => {
  const { restore, getMessageRestoreInfo } = useRestoreMessage();

  if (messages.length === 0) return null;

  const userMessageCount = messages.filter(
    (msg) => (msg as Message).role === "user",
  ).length;

  const messagePairs: Array<{ user: Message | null; assistant: Message }> = [];
  let currentUserMessage: Message | null = null;

  for (const msg of messages) {
    const message = msg as Message;
    if (message.role === "user") {
      currentUserMessage = message;
    } else if (message.role === "assistant") {
      messagePairs.push({
        user: currentUserMessage,
        assistant: message,
      });
      currentUserMessage = null;
    }
  }

  const handleRestore = async (messageId: string) => {
    const success = await restore(messageId);
    return success;
  };

  return (
    <div className="my-4 w-full pr-[8px]">
      <div className="flex items-center justify-center mb-3 relative">
        <span className="relative flex items-center gap-1.5 px-3 bg-background text-xs font-medium text-secondary-n-400 tracking-wide">
          <Clock className="w-3 h-3 text-secondary-n-400" />
          {userMessageCount} Archived Message{userMessageCount !== 1 ? "s" : ""}
        </span>
      </div>

      <Accordion
        type="single"
        collapsible
        className="border border-dashed border-secondary-n-700/30 rounded-lg bg-secondary-n-900 overflow-hidden"
      >
        <AccordionItem value="archived-messages" className="border-none">
          <AccordionTrigger className="p-3 rounded-lg transition-colors hover:bg-secondary-n-800/50 hover:no-underline [&>svg]:text-secondary-n-400">
            <div className="flex flex-col gap-1">
              <span className="flex items-center gap-1.5 text-xs font-medium text-secondary-n-400">
                <Clock className="w-3.5 h-3.5 text-secondary-n-400" />
                Explore History
              </span>
            </div>
          </AccordionTrigger>

          <AccordionContent>
            {messagePairs.length > 0 && (
              <Accordion
                type="single"
                collapsible
                className="w-full border-t border-dashed border-secondary-n-700/30 -mb-2"
              >
                {messagePairs.map((pair, index) => {
                  const versionNumber = index + 1;
                  const {
                    isOverwritten,
                    latestActivity,
                    isRestoring,
                    canRestore,
                  } = getMessageRestoreInfo(pair.assistant.id);

                  const restoreDisabled = !canRestore;

                  let tooltipText = `Restore to Version ${versionNumber}`;
                  if (isRestoring) {
                    tooltipText = "Restoring...";
                  } else if (isOverwritten) {
                    tooltipText = "Newer messages exist";
                  } else if (!latestActivity) {
                    tooltipText = "Cannot restore";
                  } else if (!canRestore) {
                    tooltipText = "Another restore in progress";
                  }

                  return (
                    <AccordionItem
                      key={pair.assistant.id}
                      value={`version-${index}`}
                      className="border-dashed border-secondary-n-700/30 bg-secondary-n-950 last:border-b-0"
                    >
                      <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-secondary-n-800/50 text-xs font-medium text-secondary-n-400">
                        <div className="flex items-center gap-2">
                          <span>Version {versionNumber}</span>
                          <div className="w-1 h-1 rounded-full bg-secondary-n-600" />
                          <span className="text-secondary-n-300 font-normal">
                            {new Date(
                              pair.assistant.created_at,
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="p-3 space-y-2 bg-background">
                          {pair.user && (
                            <ChatMessage
                              message={pair.user}
                              isLastMessage={false}
                              searchQuery={searchQuery}
                              isGreyedOut={true}
                            />
                          )}
                          <ChatMessage
                            message={pair.assistant}
                            isLastMessage={false}
                            searchQuery={searchQuery}
                            isGreyedOut={true}
                          />

                          <div className="flex items-center justify-end pt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestore(pair.assistant.id);
                              }}
                              disabled={restoreDisabled}
                              className={cn(
                                "flex items-center gap-1 px-2 py-1 rounded-md text-2xs font-medium transition-colors border",
                                restoreDisabled
                                  ? "border-secondary-n-700 bg-transparent text-secondary-n-600 cursor-default"
                                  : "border-transparent bg-[#16A34A] text-white hover:bg-[#15803D]",
                              )}
                              data-tooltip-id="tooltip"
                              data-tooltip-content={tooltipText}
                              data-tooltip-place="top"
                            >
                              {isRestoring ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3 h-3" />
                              )}
                              {isRestoring
                                ? "Restoring..."
                                : isOverwritten
                                  ? "Overwritten"
                                  : `Restore Version ${versionNumber}`}
                            </button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
